import type { CanvasRect, CanvasShotNode, CanvasView } from "@dramapie/shared";

/**
 * M6：画布 / 时间线的纯函数。
 *
 * 布局算法只在服务端（契约 M6 §1）：这里只做相机（平移 / 缩放 / 适应）
 * 与时间刻度的换算，方便单测和组件共用，不复制任何布局规则。
 */

export type WorkspaceViewMode = "storyboard" | "canvas" | "timeline";

export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 2;
export const ZOOM_STEP = 1.2;

export interface Camera {
  x: number;
  y: number;
  zoom: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Bounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 1;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

/**
 * 以 `point`（相对画布视口的坐标）为中心缩放：
 * 缩放前后，光标下的世界坐标必须落在同一个屏幕位置。
 */
export function zoomAtPoint(camera: Camera, zoom: number, point: Point): Camera {
  const next = clampZoom(zoom);
  const worldX = (point.x - camera.x) / camera.zoom;
  const worldY = (point.y - camera.y) / camera.zoom;
  return {
    zoom: next,
    x: point.x - worldX * next,
    y: point.y - worldY * next,
  };
}

/** 把矩形中心移到视口中心，缩放不变。 */
export function centerCameraOn(rect: CanvasRect, zoom: number, size: Size): Camera {
  const z = clampZoom(zoom);
  return {
    zoom: z,
    x: size.width / 2 - (rect.x + rect.w / 2) * z,
    y: size.height / 2 - (rect.y + rect.h / 2) * z,
  };
}

interface RectLike {
  x: number;
  y: number;
  w: number;
  h: number;
}

function unionBounds(rects: readonly RectLike[]): Bounds | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const rect of rects) {
    if (![rect.x, rect.y, rect.w, rect.h].every((value) => Number.isFinite(value))) continue;
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.w);
    maxY = Math.max(maxY, rect.y + rect.h);
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;
  return { x: minX, y: minY, w: Math.max(0, maxX - minX), h: Math.max(0, maxY - minY) };
}

/**
 * 没有 `sceneId` 的镜头没有服务端给的场容器，前端给一个「未分场」容器：
 * 用镜头矩形并集 + 与真实场一致的内边距（上 56 / 左右 24 / 下 24）。
 */
export function unplacedRect(shots: readonly CanvasShotNode[]): CanvasRect | null {
  const union = unionBounds(shots.map((node) => node.rect));
  if (!union) return null;
  return {
    x: union.x - 24,
    y: union.y - 56,
    w: union.w + 48,
    h: union.h + 80,
  };
}

/** 画布世界边界：所有场容器 + 未分场容器。 */
export function canvasBounds(data: CanvasView): Bounds | null {
  const rects: RectLike[] = data.scenes.map((scene) => scene.rect);
  const orphan = unplacedRect(data.unplaced);
  if (orphan) rects.push(orphan);
  return unionBounds(rects);
}

/** 适应视口：所有场容器刚好放进可视区（带内边距），缩放夹在 0.2–2。 */
export function fitCamera(bounds: Bounds | null, size: Size, padding = 40): Camera | null {
  if (!bounds || bounds.w <= 0 || bounds.h <= 0) return null;
  if (size.width <= 0 || size.height <= 0) return null;
  const usableW = Math.max(1, size.width - padding * 2);
  const usableH = Math.max(1, size.height - padding * 2);
  const zoom = clampZoom(Math.min(usableW / bounds.w, usableH / bounds.h));
  return {
    zoom,
    x: (size.width - bounds.w * zoom) / 2 - bounds.x * zoom,
    y: (size.height - bounds.h * zoom) / 2 - bounds.y * zoom,
  };
}

// ---------------------------------------------------------------- 时间线

/** 刻度间隔：让整条时间线大约 8–10 个刻度。 */
export function tickStep(total: number): number {
  if (!Number.isFinite(total) || total <= 0) return 1;
  const steps = [1, 2, 5, 10, 15, 30, 60, 120];
  return steps.find((step) => total / step <= 10) ?? 300;
}

/** 0s、5s、10s…（含末尾，末尾不严格落在整步上时也补一个）。 */
export function timelineTicks(total: number): number[] {
  if (!Number.isFinite(total) || total <= 0) return [0];
  const step = tickStep(total);
  const ticks: number[] = [];
  for (let value = 0; value <= total + 1e-6; value += step) {
    ticks.push(Math.round(value * 1000) / 1000);
  }
  if (ticks[ticks.length - 1] !== total) ticks.push(total);
  return ticks;
}

/** 时间线百分比，保留 4 位小数避免长浮点抖。 */
export function timelinePercent(value: number, total: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.round(Math.min(100, Math.max(0, (value / total) * 100)) * 10_000) / 10_000;
}

/** 在轨道上点 / 拖时，从 clientX 换算秒数。 */
export function secondsAtX(clientX: number, rect: { left: number; width: number }, total: number): number {
  if (!Number.isFinite(total) || total <= 0 || rect.width <= 0) return 0;
  const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  return Math.round(ratio * total * 100) / 100;
}
